
import * as THREE from 'three'
import GUI from 'lil-gui'
import { FontLoader } from 'three/examples/jsm/loaders/FontLoader.js'
import { TextGeometry } from 'three/examples/jsm/geometries/TextGeometry.js'
import gsap from 'gsap'
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js'
import holographicVertexShader from '../shaders/holographic/vertex.glsl'
import holographicFragmentShader from '../shaders/holographic/fragment.glsl'
import galaxyVertexShader from '../shaders/galaxy/vertex.glsl'
import galaxyFragmentShader from '../shaders/galaxy/fragment.glsl'



/**
 * Debug
 */
const gui = new GUI()
gui.close()



/**
 * Loaders
 */
const gltfLoader = new GLTFLoader()

const loadingManager = new THREE.LoadingManager(
    () => {
        gsap.to(overlayMaterial.uniforms.uAlpha, {duration: 3, value: 0})
    },
    () => {
        (itemURL, itemsLoaded, itemsTotal) => {
            console.log(itemURL, itemsLoaded, itemsTotal)
        }
    }
)



/**
 * Base
 */
// Canvas
const canvas = document.querySelector('canvas.webgl')

// Scene
const scene = new THREE.Scene()

//Ambiente Light
const ambientLight = new THREE.AmbientLight(0xffffff, 0.5)
scene.add(ambientLight)

//Directional ligh and shadows
const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
directionalLight.position.set(10, 10, 10);
directionalLight.castShadow = true;
scene.add(directionalLight);



/**
 * Overlay
 */
const overlayGeometry = new THREE.PlaneGeometry(2, 2, 1, 1)
const overlayMaterial = new THREE.ShaderMaterial({
    transparent: true,
    uniforms: {
        uAlpha: { value: 1 }
    },
    vertexShader: `
        void main() {
            gl_Position = vec4(position, 1.0);
        }
    `,
    fragmentShader: `
        uniform float uAlpha;
        void main() {
            gl_FragColor = vec4(1.0, 1.0, 1.0, uAlpha);
        }
    `
})
const overlay = new THREE.Mesh(overlayGeometry, overlayMaterial)
scene.add(overlay)



/**
 * Textures
 */
const textureLoader = new THREE.TextureLoader(loadingManager)
//const matcapTexture = textureLoader.load('/matcaps/10.png')
const particleTexture = textureLoader.load('/particles/1.png')
//matcapTexture.colorSpace = THREE.SRGBColorSpace
const smokeTexture = textureLoader.load('/smoke/0001.png')
smokeTexture.colorSpace = THREE.SRGBColorSpace



/**
 * Fonts
 */
let textMesh;
const fontLoader = new FontLoader()
fontLoader.load(
    '/fonts/Montserrat_Regular.json',
    (font) =>
    {
        const textGeometry = new TextGeometry(
            'escolhadigital',
            {
                font: font,
                size: 0.5,
                depth: 0.2,
                curveSegments: 5,
                bevelEnabled: true,
                bevelThickness: 0.03,
                bevelSize: 0.02,
                bevelOffset: 0,
                bevelSegments: 5
            }
        )

        textGeometry.center()

        const textMaterial = new THREE.MeshBasicMaterial({
            color: '#d29c6a',
            transparent: true,
            opacity: 0,
        })

        textMesh = new THREE.Mesh(textGeometry, textMaterial)

        textMesh.position.set(-1.6, -45, -22)
        textMesh.scale.set(2.02, 3.01, 0.1)
        textMesh.rotation.set(-0.80, 0, 0)

        scene.add(textMesh)

        const textMeshFolder = gui.addFolder('Text Mesh')
        textMeshFolder.addColor(textMaterial, 'color').name('textMaterial.color').onChange(() => {
            textMaterial.color.set(textMaterial.color)
        })
        textMeshFolder.add(textMesh.position, 'x').min(-3).max(3).step(0.01).name('textMesh.position.x')
        textMeshFolder.add(textMesh.position, 'y').min(-30).max(-70).step(0.01).name('textMesh.position.y')
        textMeshFolder.add(textMesh.position, 'z').min(-15).max(-30).step(0.01).name('textMesh.position.z')

        textMeshFolder.add(textMesh.rotation, 'x').min(-2).max(Math.PI * 2).step(0.01).name('textMesh.rotation.x')
        textMeshFolder.add(textMesh.rotation, 'y').min(0).max(Math.PI * 2).step(0.01).name('textMesh.rotation.y')
        textMeshFolder.add(textMesh.rotation, 'z').min(0).max(Math.PI * 2).step(0.01).name('textMesh.rotation.z')

        textMeshFolder.add(textMesh.scale, 'x').min(0.1).max(3).step(0.01).name('textMesh.scale.x')
        textMeshFolder.add(textMesh.scale, 'y').min(0.1).max(3).step(0.01).name('textMesh.scale.y')
        textMeshFolder.add(textMesh.scale, 'z').min(0.1).max(3).step(0.01).name('textMesh.scale.z')
        textMeshFolder.add(textMaterial, 'opacity').min(0).max(1).step(0.01).name('textMaterial.opacity').onChange(() => {
            textMaterial.opacity = textMaterial.opacity;
        });

    }
)



/**
 * Shaders Material
 */
const shaderMaterialParameters = {}
shaderMaterialParameters.color = '#000000'

const material = new THREE.ShaderMaterial({
    vertexShader: holographicVertexShader,
    fragmentShader: holographicFragmentShader,
    uniforms: {
        uTime: new THREE.Uniform(0),
        uColor: new THREE.Uniform(new THREE.Color(shaderMaterialParameters.color)),


    },

})



/**
 * Sizes
 */
const sizes = {
    width: window.innerWidth,
    height: window.innerHeight
}
window.addEventListener('resize', () => {
    sizes.width = window.innerWidth
    sizes.height = window.innerHeight
    camera.aspect = sizes.width / sizes.height
    camera.updateProjectionMatrix()
    renderer.setSize(sizes.width, sizes.height)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
})



/**
 * Camera
 */
const camera = new THREE.PerspectiveCamera(35, sizes.width / sizes.height, 0.1, 100)
camera.position.set(0, 0, 15)
scene.add(camera)



/**
 * Renderer
 */
const rendererParameters = {}
rendererParameters.clearColor = '#ffffff'

const renderer = new THREE.WebGLRenderer({
    canvas: canvas,
    alpha: true
})
renderer.setClearColor(rendererParameters.clearColor)
renderer.setSize(sizes.width, sizes.height)
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))



/**
 * Objects
 */
// Torus knot
const torusKnot = new THREE.Mesh(
    new THREE.TorusKnotGeometry(0.6, 0.25, 128, 32),
    material
)
torusKnot.position.x = 3
scene.add(torusKnot)

// Sphere
const sphere = new THREE.Mesh(
    new THREE.SphereGeometry(),
    material
)
sphere.position.x = - 3
scene.add(sphere)

// Suzanne
let suzanne = null
gltfLoader.load(
    '/hologram/suzanne.glb',
    (gltf) =>
    {
        suzanne = gltf.scene
        suzanne.traverse((child) =>
        {
            if(child.isMesh)
                child.material = material
        })
        scene.add(suzanne)
    }
)

const targetObjectGeometry = new THREE.BoxGeometry(1, 1, 1);
const targetObjectMaterial = new THREE.MeshToonMaterial({ color: 'red' });



/**
 * Tunnel with objects
 */
function createTunnel(center, ringCount, initialObjectsPerRing, initialRadius, radiusDecrement) {
    const tunnelGroup = new THREE.Group();

    for (let i = 0; i < ringCount; i++) {
        const radius = initialRadius - i * radiusDecrement;
        const objectsPerRing = initialObjectsPerRing - i; // Reducing de number of objects per ring
        const angleIncrement = (2 * Math.PI) / objectsPerRing; // Increment of angle for each object

        const ringGroup = new THREE.Group();

        for (let j = 0; j < objectsPerRing; j++) {
            const angle = j * angleIncrement;
            const x = radius * Math.cos(angle);
            const y = radius * Math.sin(angle);

            // geometries for the rings
            const geometries = [
                new THREE.BoxGeometry(1, 1, 1),
                new THREE.ConeGeometry(0.7, 1.5, 16),
                new THREE.TorusGeometry(0.7, 0.2, 16, 100),
                sphere.geometry,
            ];
            const geometry = geometries[Math.floor(Math.random() * geometries.length)];



            const colors = [
                new THREE.Color(0x000000),
                new THREE.Color(0xd3d3d3),
                new THREE.Color(0x3c0606)
            ];
            const color = colors[Math.floor(Math.random() * colors.length)];

            const material = new THREE.MeshToonMaterial({
                color: color,
                wireframe: Math.random() < 0.9
            });

            const mesh = new THREE.Mesh(geometry, material);
            mesh.position.set(x, y, 0);
            ringGroup.add(mesh);
        }

        ringGroup.position.set(0, 0, -i * 3);
        ringGroup.userData.radius = radius;
        tunnelGroup.add(ringGroup);
    }

    tunnelGroup.position.set(1.1, -27.5, -5.2);
    tunnelGroup.rotation.set(-0.83159, 0.118407, 0.518407);
    tunnelGroup.scale.set(2.5, 2.3, 1.9);

    scene.add(tunnelGroup);
    return tunnelGroup;
}

//Tunnel position
const tunnelCenter = new THREE.Vector3(0, -25, 0);

//Tunnel parameters
const tunnelParameters = {
    ringCount: 5,
    initialObjectsPerRing: 12,
    initialRadius: 7,
    radiusDecrement: 1,
};


const tunnelGroup = createTunnel(
    tunnelCenter,
    tunnelParameters.ringCount,
    tunnelParameters.initialObjectsPerRing,
    tunnelParameters.initialRadius,
    tunnelParameters.radiusDecrement
);

//Particles parameters
const particleParameters = {
    materialColor: '#000000',
    size: 1,
    sizeAttenuation: true,
    transparent: true,
    depthWrite: false,
    positionX: 3.8,
    positionY: -92.5,
    positionZ: -31.7,
    rotationX: 0.198407346410207,
    rotationY: 7.249916000001855,
    rotationZ: 1.55840734641021
};

//Function to create particles
function createParticles(count, size, area) {
    const particleGeometry = new THREE.BufferGeometry();
    const particlePositions = new Float32Array(count * 3);

    for (let i = 0; i < count; i++) {
        const i3 = i * 3;
        particlePositions[i3 + 0] = (Math.random() - 0.5) * area; //  X
        particlePositions[i3 + 1] = (Math.random() - 0.5) * area; //  Y
        particlePositions[i3 + 2] = (Math.random() - 0.5) * area; //  Z
    }

    particleGeometry.setAttribute('position', new THREE.BufferAttribute(particlePositions, 3));

    const particleMaterial = new THREE.PointsMaterial({
        color: particleParameters.materialColor,
        size: size,
        sizeAttenuation: particleParameters.sizeAttenuation,
        transparent: particleParameters.transparent,
        depthWrite: particleParameters.depthWrite,
        alphaTest: 0.001,
        alphaMap: particleTexture
    });

    return new THREE.Points(particleGeometry, particleMaterial);
}

// Create particles and add them to the scene
const sceneParticles = createParticles(10000, particleParameters.size, 100);
sceneParticles.position.set(particleParameters.positionX, particleParameters.positionY, particleParameters.positionZ);
sceneParticles.rotation.set(particleParameters.rotationX, particleParameters.rotationY, particleParameters.rotationZ);
scene.add(sceneParticles);


//Smoke
const smokeGeometry = new THREE.PlaneGeometry(25, 25)
const smokeMaterial = new THREE.MeshLambertMaterial({

    map: smokeTexture,
    transparent: true,
    opacity: 0.90,
    depthWrite : false
})

const smokeGroup = new THREE.Group();
scene.add(smokeGroup);

let smokeParticles = []

for (let i = 0; i < 10; i++){
    let smokeElement = new THREE.Mesh(smokeGeometry, smokeMaterial)

    smokeElement.scale.set(2, 2, 2)
    smokeElement.position.set(
        -4.31 + (Math.random() * 2 - 1), // Randomize around the x position
        -67 + (Math.random() * 2 - 1),   // Randomize around the y position
        -38.5 + (Math.random() * 2 - 1)  // Randomize around the z position
    )
    smokeElement.rotation.z = Math.random() * 360

    smokeGroup.add(smokeElement)
    smokeParticles.push(smokeElement)
}

//Function to animate smoke
function animateSmoke() {
    smokeParticles.forEach(particle => {
        particle.rotation.z += 0.001;
        particle.position.y += 0.01 * Math.sin(Date.now() * 0.001);

    });
}



/**
 * Galaxy
 */
const galaxyParameters = {
    count: 400000, 
    size: 0.24,
    radius: 15, //28,
    branches: 2,
    spin: 0.04, 
    randomness: 1.34,
    randomnessPower: 10,
    insideColor: '#0d0d0d',
    outsideColor: '#eca713',
    positionX: -6.86, //-3.25
    positionY: -128.98,
    positionZ: -90.82,
    rotationX: -3.14, 
    rotationY: 0,
    rotationZ: 0 //-1.72
    
}

let galaxyGeometry = null
let galaxyMaterial = null
let galaxyPoints = null

const generateGalaxy = () => 
    {
        if(galaxyPoints !== null)
        {
            galaxyGeometry.dispose()
            galaxyMaterial.dispose()
            scene.remove(galaxyPoints)
        }

        galaxyGeometry = new THREE.BufferGeometry()

        const positions = new Float32Array(galaxyParameters.count * 3)
        const galaxyColors = new Float32Array(galaxyParameters.count * 3)
        const galaxyScales = new Float32Array(galaxyParameters.count * 1)
        const galaxyRandomness = new Float32Array(galaxyParameters.count * 3)

        const colorInside = new THREE.Color(galaxyParameters.insideColor)
        const colorOutside = new THREE.Color(galaxyParameters.outsideColor)
      

        for(let i = 0; i < galaxyParameters.count; i++)
        {
            const i3 = i * 3

            const galaxyRaidus = Math.random() * galaxyParameters.radius
            const spinAngle = galaxyRaidus * galaxyParameters.spin
            const branchAngle = ((i % galaxyParameters.branches) / galaxyParameters.branches) * Math.PI * 2

           
            //Position
     
            positions[i3] = Math.cos(branchAngle + spinAngle) * galaxyRaidus 
            positions[i3 + 1] = 0
            positions[i3 + 2] = Math.sin(branchAngle + spinAngle) * galaxyRaidus 


            //Randomness
            const randomX = Math.pow(Math.random(), galaxyParameters.randomnessPower) * (Math.random() < 0.5 ? 1 : -1)
            const randomY = Math.pow(Math.random(), galaxyParameters.randomnessPower) * (Math.random() < 0.5 ? 1 : -1)
            const randomZ = Math.pow(Math.random(), galaxyParameters.randomnessPower) * (Math.random() < 0.5 ? 1 : -1)

            galaxyRandomness[i3] = randomX
            galaxyRandomness[i3 + 1] = randomY
            galaxyRandomness[i3 + 2] = randomZ

            //Color
            const mixedColor = colorInside.clone()
            mixedColor.lerp(colorOutside, (galaxyRaidus / galaxyParameters.radius))
            galaxyColors[i3] = mixedColor.r
            galaxyColors[i3 + 1] = mixedColor.g
            galaxyColors[i3 + 2] = mixedColor.b
            

            //Scale
            galaxyScales[i] = Math.random() 
           
        }

        galaxyGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))
        galaxyGeometry.setAttribute('color', new THREE.BufferAttribute(galaxyColors, 3))
        galaxyGeometry.setAttribute('aScale', new THREE.BufferAttribute(galaxyColors, 1))
        galaxyGeometry.setAttribute('aRandomness', new THREE.BufferAttribute(galaxyRandomness, 3))
       

        //Material
        galaxyMaterial = new THREE.ShaderMaterial({
          
            depthWrite: false,
        //    blending: THREE.AdditiveBlending,
           vertexColors: true,
           vertexShader: galaxyVertexShader,
           fragmentShader: galaxyFragmentShader,
              uniforms: {
                uTime: { value: 0 },
                uSize: { value: 100 * renderer.getPixelRatio() },
                

            }
    })

        //points
        galaxyPoints = new THREE.Points(galaxyGeometry, galaxyMaterial);
        galaxyPoints.position.set(galaxyParameters.positionX, galaxyParameters.positionY, galaxyParameters.positionZ);
        galaxyPoints.rotation.set(galaxyParameters.rotationX, galaxyParameters.rotationY, galaxyParameters.rotationZ);
        scene.add(galaxyPoints)
    }

generateGalaxy()



/**
 * GUI
 */
//Controls Suzanne
const suzanneFolder = gui.addFolder('Suzanne');
suzanneFolder
    .addColor(shaderMaterialParameters, 'color')
    .onChange(() =>
    {
        material.uniforms.uColor.value.set(shaderMaterialParameters.color)
    })

//Controls for renderer
const rendererFolder = gui.addFolder('Renderer');
rendererFolder
    .addColor(rendererParameters, 'clearColor')
    .onChange(() =>
    {
        renderer.setClearColor(rendererParameters.clearColor)
    })


//Controls for camera
const cameraFolder = gui.addFolder('Camera');
cameraFolder.add(camera.position, 'x', -10, 10, 0.01).name('Camera X')
cameraFolder.add(camera.position, 'y', -10, 10, 0.01).name('Camera Y')
cameraFolder.add(camera.position, 'z', -10, 10, 0.01).name('Camera Z')
cameraFolder.add(camera.rotation, 'x', -Math.PI, Math.PI, 0.01).name('Camera Rotation X')

//Controls for Particles
const sceneParticlesFolder = gui.addFolder('Scene Particles');
sceneParticlesFolder.add(sceneParticles.material, 'size', 0.01, 1, 0.01).name('Size');
sceneParticlesFolder.addColor(sceneParticles.material, 'color').name('Color');
sceneParticlesFolder.add(sceneParticles.position, 'x', -100, 100, 0.1).name('Position X');
sceneParticlesFolder.add(sceneParticles.position, 'y', -100, 100, 0.1).name('Position Y');
sceneParticlesFolder.add(sceneParticles.position, 'z', -100, 100, 0.1).name('Position Z');
sceneParticlesFolder.add(sceneParticles.rotation, 'x', -Math.PI, Math.PI, 0.01).name('Rotation X');
sceneParticlesFolder.add(sceneParticles.rotation, 'y', -Math.PI, Math.PI, 0.01).name('Rotation Y');
sceneParticlesFolder.add(sceneParticles.rotation, 'z', -Math.PI, Math.PI, 0.01).name('Rotation Z');

const particlesFolder = gui.addFolder('Particles');
particlesFolder.add(sceneParticles.rotation, 'y', -Math.PI, Math.PI, 0.01).name('Global Rotation Y');
particlesFolder.add(sceneParticles.rotation, 'x', -Math.PI, Math.PI, 0.01).name('Global Rotation X');

const particlesMaterialFolder = gui.addFolder('Particles Material');
particlesMaterialFolder.addColor(particleParameters, 'materialColor').name('Color').onChange(() => {
    sceneParticles.material.color.set(particleParameters.materialColor);
});
particlesMaterialFolder.add(particleParameters, 'size', 0.01, 1, 0.01).name('Size').onChange(() => {
    sceneParticles.material.size = particleParameters.size;
});
particlesMaterialFolder.add(particleParameters, 'sizeAttenuation').name('Size Attenuation').onChange(() => {
    sceneParticles.material.sizeAttenuation = particleParameters.sizeAttenuation;
});
particlesMaterialFolder.add(particleParameters, 'transparent').name('Transparent').onChange(() => {
    sceneParticles.material.transparent = particleParameters.transparent;
});
particlesMaterialFolder.add(particleParameters, 'depthWrite').name('Depth Write').onChange(() => {
    sceneParticles.material.depthWrite = particleParameters.depthWrite;
});

//Controls for Tunnel
const tunnelFolder = gui.addFolder('Tunnel Group Parameters');
tunnelFolder.add(tunnelGroup.position, 'x', -20, 20, 0.1).name('Position X');
tunnelFolder.add(tunnelGroup.position, 'y', -35, 20, 0.1).name('Position Y');
tunnelFolder.add(tunnelGroup.position, 'z', -50, 10, 0.1).name('Position Z');
tunnelFolder.add(tunnelGroup.rotation, 'x', -Math.PI, Math.PI, 0.01).name('Rotation X');
tunnelFolder.add(tunnelGroup.rotation, 'y', -Math.PI, Math.PI, 0.01).name('Rotation Y');
tunnelFolder.add(tunnelGroup.rotation, 'z', -Math.PI, Math.PI, 0.01).name('Rotation Z');
tunnelFolder.add(tunnelGroup.scale, 'x', 0.1, 5, 0.1).name('Scale X');
tunnelFolder.add(tunnelGroup.scale, 'y', 0.1, 5, 0.1).name('Scale Y');
tunnelFolder.add(tunnelGroup.scale, 'z', 0.1, 5, 0.1).name('Scale Z');

//Controls for Smoke
const smokeGroupFolder = gui.addFolder('Smoke Group');
smokeGroupFolder.add(smokeGroup.position, 'x', -100, 100, 0.01).name('Group Position X');
smokeGroupFolder.add(smokeGroup.position, 'y', -100, 100, 0.01).name('Group Position Y');
smokeGroupFolder.add(smokeGroup.position, 'z', -100, 100, 0.01).name('Group Position Z');
smokeGroupFolder.add(smokeGroup.rotation, 'x', -Math.PI, Math.PI, 0.01).name('Group Rotation X');
smokeGroupFolder.add(smokeGroup.rotation, 'y', -Math.PI, Math.PI, 0.01).name('Group Rotation Y');
smokeGroupFolder.add(smokeGroup.rotation, 'z', -Math.PI, Math.PI, 0.01).name('Group Rotation Z');
smokeGroupFolder.add(smokeGroup.scale, 'x', 0.1, 5, 0.1).name('Group Scale X');
smokeGroupFolder.add(smokeGroup.scale, 'y', 0.1, 5, 0.1).name('Group Scale Y');
smokeGroupFolder.add(smokeGroup.scale, 'z', 0.1, 5, 0.1).name('Group Scale Z');

const galaxyFolder = gui.addFolder('Galaxy');
galaxyFolder.add(galaxyParameters, 'count').min(1000).max(400000).step(100).onFinishChange(generateGalaxy)
galaxyFolder.add(galaxyParameters, 'size').min(0.01).max(20).step(0.01).onFinishChange(generateGalaxy)
galaxyFolder.add(galaxyParameters, 'radius').min(1).max(40).step(1).onFinishChange(generateGalaxy)
galaxyFolder.add(galaxyParameters, 'branches').min(2).max(30).step(1).onFinishChange(generateGalaxy)
galaxyFolder.add(galaxyParameters, 'spin').min(-5).max(10).step(0.01).onFinishChange(generateGalaxy)
galaxyFolder.add(galaxyParameters, 'randomness').min(0).max(3).step(0.01).onFinishChange(generateGalaxy)
galaxyFolder.add(galaxyParameters, 'randomnessPower').min(1).max(20).step(0.01).onFinishChange(generateGalaxy)
galaxyFolder.addColor(galaxyParameters, 'insideColor').onFinishChange(generateGalaxy)
galaxyFolder.addColor(galaxyParameters, 'outsideColor').onFinishChange(generateGalaxy)
galaxyFolder.add(galaxyParameters, 'positionX').min(-100).max(100).step(0.01).name('Position X').onChange(() => {
    galaxyPoints.position.x = galaxyParameters.positionX;
});
galaxyFolder.add(galaxyParameters, 'positionY').min(-200).max(100).step(0.01).name('Position Y').onChange(() => {
    galaxyPoints.position.y = galaxyParameters.positionY;
});
galaxyFolder.add(galaxyParameters, 'positionZ').min(-100).max(100).step(0.01).name('Position Z').onChange(() => {
    galaxyPoints.position.z = galaxyParameters.positionZ;
});
galaxyFolder.add(galaxyParameters, 'rotationX').min(-10).max(Math.PI).step(0.01).name('Rotation X').onChange(() => {
    galaxyPoints.rotation.x = galaxyParameters.rotationX;
});
galaxyFolder.add(galaxyParameters, 'rotationY').min(-10).max(Math.PI).step(0.01).name('Rotation Y').onChange(() => {
    galaxyPoints.rotation.y = galaxyParameters.rotationY;
});
galaxyFolder.add(galaxyParameters, 'rotationZ').min(-10).max(Math.PI).step(0.01).name('Rotation Z').onChange(() => {
    galaxyPoints.rotation.z = galaxyParameters.rotationZ;
});

// Controls for last camera position
const lastCameraPositionFolder = gui.addFolder('Last Camera Position');
lastCameraPositionFolder.add(camera.position, 'x', -200, 200, 0.1).name('Camera X');
lastCameraPositionFolder.add(camera.position, 'y', -200, 200, 0.1).name('Camera Y');
lastCameraPositionFolder.add(camera.position, 'z', -200, 200, 0.1).name('Camera Z');
lastCameraPositionFolder.add(camera.rotation, 'x', -Math.PI, Math.PI, 0.01).name('Rotation X');
lastCameraPositionFolder.add(camera.rotation, 'y', -Math.PI, Math.PI, 0.01).name('Rotation Y');
lastCameraPositionFolder.add(camera.rotation, 'z', -Math.PI, Math.PI, 0.01).name('Rotation Z');



/**
 * Animate
 */
const targetPosition = {x: 0, y: 0, z: 15 };

const clock = new THREE.Clock()
const tick = () =>
    {
        const elapsedTime = clock.getElapsedTime()

        //Update hologram material
        material.uniforms.uTime.value = elapsedTime

        //Update galaxy material
        galaxyMaterial.uniforms.uTime.value = elapsedTime

        // Rotate objects
        if(suzanne)
        {

            suzanne.rotation.y = elapsedTime * 0.2
        }

        sphere.rotation.x = - elapsedTime * 0.1


        torusKnot.rotation.x = - elapsedTime * 0.1
        torusKnot.rotation.y = elapsedTime * 0.2


         // Animate tunnel objects
         animateTunnelRings(tunnelGroup, elapsedTime);

         function animateTunnelRings(group, elapsedTime) {
            const baseRadius = tunnelParameters.initialRadius;
            const rotationSpeedFactor = 0.03; //Rotation of the rings

            group.children.forEach((ring) => {
                const radius = ring.userData.radius;
                const speed = (baseRadius / radius) * rotationSpeedFactor;
                ring.rotation.z = elapsedTime * speed;

                // Rotation of the objects inside the rings
                ring.children.forEach((object) => {
                    object.rotation.x = elapsedTime * 0.05;
                    object.rotation.y = elapsedTime * 0.06;
                });
            });
        }

        // Rotate particles
        sceneParticles.rotation.y = elapsedTime * 0.01;

        // Check camera position and update textMesh visibility
        if (textMesh) {
            const distanceToTarget = camera.position.distanceTo(new THREE.Vector3(targetPosition.x, targetPosition.y, targetPosition.z));

            if (distanceToTarget < 17) {
                gsap.to(textMesh.material, { opacity: 0, duration: 1 });
            } else {
                gsap.to(textMesh.material, { opacity: 1, duration: 1 });
            }
        }

        // Update smoke
        animateSmoke();

        // Render
        renderer.render(scene, camera)

        // Call tick again on the next frame
        window.requestAnimationFrame(tick)



    }

tick()

// Event listeners to header links
const headerLinks = document.querySelectorAll('header nav a');
headerLinks.forEach(link => {
    link.addEventListener('click', (event) => {
        event.preventDefault();
        const targetUrl = event.target.href;

        // Start the overlay animation
        gsap.to('#white-overlay', {
            duration: 1,
            opacity: 1,
            onComplete: () => {
                window.location.href = targetUrl;
            }
        });
    });
});

window.addEventListener('load', () => {
    document.body.classList.add('loaded');
});

// Fixed position for camera
const cameraPositions = [
    { x: 0, y: 0, z: 15 }, // orignal position
    { x: 0, y: -25, z: -6 },
    { x: -1.6, y: -45, z: -24 },
    { x: -1.6, y: -75, z: -45.5 }, 
    { x: -1.6, y: -124.5, z: -70.4, rotationX: -0.251592653589793, rotationY: 0.0484073464102068, rotationZ: 0.288407346410207},
    //{ x: -1.77, y: -128.98, z: -90.82 } // Last camara position
      
];



// actual camera position index
let currentPositionIndex = 0;

let isRotated = false;


// function to move camera to a specific position
function moveCameraToPosition(index) {
    const position = cameraPositions[index];
    gsap.to(camera.position, {
        x: position.x,
        y: position.y,
        z: position.z,
        duration: 1.5,
        ease: "power2.out",        
    });
    if (index === cameraPositions.length - 1 && position.rotationX !== undefined) {
        gsap.to(camera.rotation, {
            x: position.rotationX,
            y: position.rotationY,
            z: position.rotationZ,
            duration: 1.5,
            ease: "power2.out",
        });
    }
}


// scroll
let isAnimating = false
window.addEventListener('wheel', (event) => {
    if (isAnimating) return
    if (!isRotated) {
        // first scroll
        if (event.deltaY > 0) {
            gsap.to(camera.rotation, {
                x: -0.90,
                duration: 1.5,
                ease: "power2.out",
                onComplete: () => {
                    isAnimating = false; 
                },
            });
            isRotated = true; // rotation done
        }
    } else {
        if (event.deltaY > 0 && currentPositionIndex < cameraPositions.length - 1) {
            // scroll forwad: move camera to next position
            isAnimating = true;
            currentPositionIndex++
            moveCameraToPosition(currentPositionIndex)
            console.log(currentPositionIndex)

            gsap.delayedCall(1.5, () => {
                isAnimating = false
            })

        } else if (event.deltaY < 0) {
            if (currentPositionIndex > 0) {
                // scroll back: move camera to previous position
                isAnimating = true
                currentPositionIndex--
                moveCameraToPosition(currentPositionIndex);
                console.log(currentPositionIndex)
                gsap.delayedCall(1.5, () => {
                    isAnimating = false
                })
                if (currentPositionIndex < cameraPositions.length - 1) {
                    gsap.to(camera.rotation, {
                        x: -0.90,
                        y: 0,
                        z: 0,
                        duration: 1.5,
                        ease: "power2.out",
                    });
                }
            } else if (isRotated) {
                // scroll back to original position
                isAnimating = true
                gsap.to(camera.rotation, {
                    x: 0,
                    duration: 1.5,
                    ease: "power2.out",
                    onComplete: () => {
                        isAnimating = false
                    },
                });
                isRotated = false

            }
        }
    }
});


document.addEventListener("DOMContentLoaded", () => {
    const header = document.querySelector("header");

    window.addEventListener("scroll", () => {
        if (window.scrollY > 50) {
            header.classList.add("scrolled");
        } else {
            header.classList.remove("scrolled");
        }
    });
});